import React from 'react'
import styled from 'styled-components'
import breakpoint from 'src/utils/styledComponentsBreakpoint'
import { applyUploadcareModifiers } from '@jarvis.oddle/core/tools/uploadcare'
import {
  borderRadiusSizes,
  colorConstants,
  marginSizes,
  padding,
  paddingSizes
} from '@jarvis.oddle/core/theme/default'
import Div from '@jarvis.oddle/core/components/Div'
import Card from '@jarvis.oddle/core/components/Card'
import Flex from '@jarvis.oddle/core/components/Flex'
import Image from '@jarvis.oddle/core/components/Image'
import { H1, LinkText } from '@jarvis.oddle/core/components/Typo'
import Modal, { CloseButton } from '@jarvis.oddle/core/components/Modal'
import richTextHtmlParser from '@jarvis.oddle/core/components/TextEditor/parser'
import { MobileHidden } from 'src/components/core/Responsive'

const PopupContainer = styled(Card).attrs(() => ({
  borderRadius: borderRadiusSizes.medium
}))`
  display: flex;
  flex-direction: column;
  min-height: 400px;
  max-height: 90vh;
  width: 350px;
  max-width: calc(100vw - ${padding[paddingSizes.medium]});
  position: absolute;
  bottom: 20px;
  top: unset;
  left: 50%;
  transform: translateX(-50%);
  overflow: auto;
  && {
    padding: ${padding[paddingSizes.none]};
  }

  ${breakpoint('desktop')`
    width: 500px;
    top: 50%;
    bottom: unset;
    transform: translateX(-50%) translateY(-50%);
  `}
`

const PopupContentContainer = styled(Flex).attrs(() => ({
  flexGrow: 1,
  flexShrink: 0,
  flexDirection: 'column',
  alignItems: 'center',
  padding: paddingSizes.large,
  paddingTop: paddingSizes.extraLarge
}))`
  ${breakpoint('desktop')`
    padding: ${padding[paddingSizes.extraLarge]}
  `}
`

const PopupCloseButton = styled(CloseButton)`
  background-color: ${(props) => props.theme.colorContainerColor};
  top: 10px;
  right: 10px;
  transform: translateX(0);
`

const imageHeightWidthRatio = 5 / 12

const FixedRatioContainer = styled(Div)`
  position: relative;
  width: 100%;
  padding-top: ${imageHeightWidthRatio * 100}%;
  background-color: ${colorConstants.gray};
`

const StyledH1 = styled(H1)`
  word-break: break-word;
`

const StyledLinkedText = styled(LinkText).attrs((props) => ({
  color: props.theme.colorBodyFont,
  marginBottom: marginSizes.none
}))``

const StyledImage = styled(Image)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`

const BodyContainer = styled(Div)`
  width: 100%;
`

const PopupImage = (props) => (
  <MobileHidden>
    <FixedRatioContainer>
      <StyledImage {...props} />
    </FixedRatioContainer>
  </MobileHidden>
)

interface PopupProps {
  popupImage: boolean | string
  headerText: string
  bodyText: string
  closeButtonText: string
  body?: React.ReactNode | null
  onClose: () => void
}

const Popup = ({
  popupImage,
  headerText,
  bodyText,
  closeButtonText,
  body = null,
  onClose,
  ...props
}: PopupProps): JSX.Element => {
  const parsedBodyText = richTextHtmlParser(bodyText)

  return (
    <Modal
      {...props}
      onClose={onClose}
      withoutBackdrop
    >
      <PopupContainer>
        {/* NOTE: Progressive loading only works for jpeg images, so we need to format images to jpeg here. */}
        {popupImage && (
          <PopupImage
            src={applyUploadcareModifiers(popupImage, {
              format: 'jpeg',
              progressive: 'yes'
            })}
          />
        )}
        <PopupContentContainer>
          <StyledH1 textAlign='center' normalised bold>
            {headerText}
          </StyledH1>
          <BodyContainer>{parsedBodyText}</BodyContainer>
          <BodyContainer>{body}</BodyContainer>
          {closeButtonText && (
            <Flex
              marginTop={marginSizes.medium}
              flexGrow={1}
              flexShrink={0}
              alignItems='flex-end'
            >
              <StyledLinkedText onClick={onClose}>
                {closeButtonText}
              </StyledLinkedText>
            </Flex>
          )}
        </PopupContentContainer>
        <PopupCloseButton onClick={onClose} />
      </PopupContainer>
    </Modal>
  )
}

export default Popup
